<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- ── DESKTOP HEADER ── -->
      <div class="hidden md:flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <Link :href="`/khata`"
            class="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition">
            <svg class="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
            </svg>
          </Link>
          <div>
            <h1 class="text-2xl font-bold text-slate-800 dark:text-white">{{ customer.name }}</h1>
            <p class="text-slate-500 dark:text-slate-400 text-sm">{{ customer.shop_name }}</p>
          </div>
        </div>

        <!-- Balance Card (Desktop) -->
        <div v-if="!dataLoading" class="shrink-0">
          <div v-if="currentBalance >= 0" class="bg-white dark:bg-slate-900 border border-red-100 dark:border-red-900 rounded-xl px-5 py-3 text-right">
            <p class="text-[10px] font-semibold text-red-400 uppercase tracking-wide">Maine Lene Hain</p>
            <p class="text-2xl font-bold text-red-500 dark:text-red-400">Rs {{ formatNum(currentBalance) }}</p>
          </div>
          <div v-else class="bg-white dark:bg-slate-900 border border-emerald-100 dark:border-emerald-900 rounded-xl px-5 py-3 text-right">
            <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">Maine Dene Hain</p>
            <p class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(Math.abs(currentBalance)) }}</p>
          </div>
        </div>
        <div v-else class="h-16 w-52 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
      </div>

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-4">
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-4">
          <div class="flex items-center gap-3 mb-3">
            <Link :href="`/khata`"
              class="p-2 hover:bg-white/10 rounded-lg transition">
              <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
              </svg>
            </Link>
            <div class="flex-1">
              <h1 class="text-lg font-bold text-white">{{ customer.name }}</h1>
              <p v-if="customer.phone_number" class="text-indigo-100 text-xs mt-0.5">{{ customer.phone_number }}</p>
            </div>
          </div>

          <!-- Balance Card -->
          <div v-if="!dataLoading" class="bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-3 text-center">
            <p class="text-xs font-bold text-white/80 uppercase tracking-widest mb-1">
              {{ currentBalance >= 0 ? 'Maine Lene Hain' : 'Maine Dene Hain' }}
            </p>
            <p class="text-3xl font-bold text-white">Rs {{ formatNum(Math.abs(currentBalance)) }}</p>
          </div>
          <div v-else class="bg-white/15 rounded-2xl h-20 animate-pulse"></div>
        </div>
      </div>

      <!-- Flash messages -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>

      <!-- Action buttons (Desktop & Mobile) -->
      <div class="flex gap-2 mb-4">
        <button @click="openAdd('sent')"
          class="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-3 rounded-xl shadow-sm transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 19.5v-15m0 0l-6.75 6.75M12 4.5l6.75 6.75"/>
          </svg>
          Maine Diye
        </button>
        <button @click="openAdd('received')"
          class="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-3 rounded-xl shadow-sm transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m0 0l6.75-6.75M12 19.5l-6.75-6.75"/>
          </svg>
          Maine Liye
        </button>
      </div>

      <!-- ── DESKTOP TABLE ── -->
      <div class="hidden md:block">
        <!-- Skeleton -->
        <div v-if="dataLoading" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-8">
            <div v-for="i in 4" :key="i" class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 8" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
          </div>
        </div>

        <!-- Table -->
        <div v-else class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-100 dark:border-slate-800">
                <th class="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-5 py-3.5">Date</th>
                <th class="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-5 py-3.5">Maine Diye</th>
                <th class="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-5 py-3.5">Maine Liye</th>
                <th class="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-5 py-3.5">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50 dark:divide-slate-800/50">
              <tr v-for="transaction in transactions" :key="transaction.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                <td class="px-5 py-4">
                  <p class="text-sm font-medium text-slate-800 dark:text-white">{{ formatDate(transaction.target_date) }}</p>
                  <p class="text-xs font-medium mt-0.5"
                    :class="transaction.balance >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
                    Bal: Rs {{ formatNum(Math.abs(transaction.balance)) }}
                  </p>
                </td>
                <td class="px-5 py-4 text-right">
                  <div v-if="transaction.sent > 0">
                    <p class="text-sm font-semibold text-red-600 dark:text-red-400">
                      Rs {{ formatNum(transaction.sent) }}
                    </p>
                    <p v-if="transaction.bank_currency && transaction.bank_currency !== 'pkr' && transaction.bank_currency !== 0" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {{ formatNum(transaction.original_amount) }} {{ getCurrencySymbol(transaction.bank_currency) }}
                    </p>
                  </div>
                  <span v-else class="text-sm text-slate-300 dark:text-slate-700">-</span>
                </td>
                <td class="px-5 py-4 text-right">
                  <div v-if="transaction.received > 0">
                    <p class="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                      Rs {{ formatNum(transaction.received) }}
                    </p>
                    <p v-if="transaction.bank_currency && transaction.bank_currency !== 'pkr' && transaction.bank_currency !== 0" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {{ formatNum(transaction.original_amount) }} {{ getCurrencySymbol(transaction.bank_currency) }}
                    </p>
                  </div>
                  <span v-else class="text-sm text-slate-300 dark:text-slate-700">-</span>
                </td>
                <td class="px-5 py-4">
                  <div class="flex items-center justify-center gap-2">
                    <button @click="openEdit(transaction)"
                      class="p-1.5 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950 rounded-lg transition">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                      </svg>
                    </button>
                    <button @click="openDeleteConfirm(transaction)"
                      class="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="transactions.length === 0">
                <td colspan="4" class="px-5 py-12 text-center text-slate-400 dark:text-slate-500 text-sm">
                  No transactions yet
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── MOBILE TABLE ── -->
      <div class="md:hidden">
        <!-- Skeleton -->
        <template v-if="dataLoading">
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <!-- Header -->
            <div class="grid grid-cols-3 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700">
              <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
              <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
              <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
            </div>
            <!-- Rows -->
            <div v-for="i in 6" :key="i" class="border-b border-slate-100 dark:border-slate-800 px-4 py-4">
              <div class="grid grid-cols-3 gap-4">
                <div class="space-y-2">
                  <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                  <div class="h-2 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                </div>
                <div class="h-3 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                <div class="h-3 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Table -->
        <template v-else>
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <!-- Header -->
            <div class="grid grid-cols-3 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700">
              <div class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Date</div>
              <div class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">Maine diye</div>
              <div class="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">Maine Liye</div>
            </div>

            <!-- Rows -->
            <div v-for="transaction in transactions" :key="transaction.id"
              @click="toggleExpand(transaction.id)"
              class="border-b border-slate-100 dark:border-slate-800 last:border-b-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">

              <!-- Main row -->
              <div class="grid grid-cols-3 gap-4 px-4 py-4 items-start">
                <!-- Date column -->
                <div>
                  <p class="text-sm font-medium text-slate-800 dark:text-white">
                    {{ formatDateShort(transaction.target_date) }}
                  </p>
                  <p class="text-xs font-medium"
                    :class="transaction.balance >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
                    Bal. Rs {{ formatNum(Math.abs(transaction.balance)) }}
                  </p>
                </div>

                <!-- Maine Diye column -->
                <div class="text-right">
                  <div v-if="transaction.sent > 0">
                    <p class="text-base font-semibold text-red-600 dark:text-red-400">
                      Rs. {{ formatNum(transaction.sent) }}
                    </p>
                    <p v-if="transaction.bank_currency && transaction.bank_currency !== 'pkr' && transaction.bank_currency !== 0" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {{ formatNum(transaction.original_amount) }} {{ getCurrencySymbol(transaction.bank_currency) }}
                    </p>
                  </div>
                </div>

                <!-- Maine Liye column -->
                <div class="text-right">
                  <div v-if="transaction.received > 0">
                    <p class="text-base font-semibold text-emerald-600 dark:text-emerald-400">
                      Rs. {{ formatNum(transaction.received) }}
                    </p>
                    <p v-if="transaction.bank_currency && transaction.bank_currency !== 'pkr' && transaction.bank_currency !== 0" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {{ formatNum(transaction.original_amount) }} {{ getCurrencySymbol(transaction.bank_currency) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Expanded details -->
              <div v-if="expandedId === transaction.id"
                @click.stop
                class="px-4 pb-4 pt-2 bg-slate-50 dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800">
                <div class="space-y-3">
                  <!-- Source -->
                  <div v-if="transaction.source" class="flex justify-between text-sm">
                    <span class="text-slate-500 dark:text-slate-400">Source:</span>
                    <span class="text-slate-800 dark:text-white font-medium capitalize">{{ transaction.source.replace(/_/g, ' ') }}</span>
                  </div>

                  <!-- Bank -->
                  <div v-if="transaction.bank_name" class="flex justify-between text-sm">
                    <span class="text-slate-500 dark:text-slate-400">Bank:</span>
                    <span class="text-slate-800 dark:text-white font-medium">{{ transaction.bank_name }}</span>
                  </div>

                  <!-- Notes -->
                  <div v-if="transaction.notes" class="text-sm">
                    <p class="text-slate-500 dark:text-slate-400 mb-1">Notes:</p>
                    <p class="text-slate-800 dark:text-white">{{ transaction.notes }}</p>
                  </div>

                  <!-- Proof -->
                  <div v-if="transaction.proof_url" class="text-sm">
                    <p class="text-slate-500 dark:text-slate-400 mb-2">Proof:</p>
                    <div @click.stop="openProof(transaction.proof_url)"
                      class="relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-90 transition">
                      <img :src="transaction.proof_url" alt="Proof" class="w-full h-auto" />
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="flex gap-2 pt-2">
                    <button @click.stop="openEdit(transaction)"
                      class="flex-1 flex items-center justify-center gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white
                             text-sm font-semibold px-3 py-2 rounded-lg transition">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                      </svg>
                      Edit
                    </button>
                    <button @click.stop="openDeleteConfirm(transaction)"
                      class="flex-1 flex items-center justify-center gap-1.5 bg-red-500 hover:bg-red-600 text-white
                             text-sm font-semibold px-3 py-2 rounded-lg transition">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="transactions.length === 0" class="px-4 py-12 text-center text-slate-400 dark:text-slate-500 text-sm">
              No transactions yet
            </div>
          </div>
        </template>
      </div>

    </div>

    <!-- ── ADD / EDIT MODAL ── -->
    <Modal v-if="showModal" @close="closeModal">
      <template #title>{{ editTarget ? 'Edit Khata Entry' : (modalAction === 'sent' ? 'Maine Diye' : 'Maine Liye') }}</template>
      <form @submit.prevent="submitForm" class="space-y-4">

        <!-- Amount -->
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Amount <span class="text-red-400">*</span></label>
          <input v-model="form.amount" type="number" step="1" min="1" required
            class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
            placeholder="Enter amount" />
        </div>

        <!-- More Details Toggle -->
        <button type="button" @click="showMoreDetails = !showMoreDetails"
          class="flex items-center gap-2 text-sm font-medium text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition">
          <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': showMoreDetails }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
          {{ showMoreDetails ? 'Hide Details' : 'More Details' }}
        </button>

        <!-- Additional Fields (collapsible) -->
        <div v-show="showMoreDetails" class="space-y-4 pt-2">
          <!-- Source -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Source</label>
            <div class="relative">
              <select v-model="form.source"
                class="w-full appearance-none bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-4 pr-9 py-3 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer">
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="third_party_payment">Third Party Payment</option>
              </select>
              <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
          </div>

          <!-- Bank (if bank_transfer) -->
          <div v-if="form.source === 'bank_transfer'">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Bank</label>
            <div class="relative">
              <select v-model="form.bank_id"
                class="w-full appearance-none bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-4 pr-9 py-3 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer">
                <option value="">Select bank</option>
                <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
              </select>
              <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
          </div>

          <!-- Date -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Date</label>
            <input v-model="form.target_date" type="date"
              class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition" />
          </div>

          <!-- Notes -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notes</label>
            <textarea v-model="form.notes" rows="3"
              class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition resize-none"
              placeholder="Optional notes"></textarea>
          </div>

          <!-- Proof Upload -->
          <ProofUpload
            v-model="proofFile"
            :existing-proof-url="proofPreviewUrl"
            input-id="khata-proof-input"
            @view-proof="openProof"
          />
        </div>

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeModal"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="loading"
            class="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition"
            :class="loading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-600'">
            {{ loading ? 'Saving...' : (editTarget ? 'Update' : 'Add') }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── PROOF MODAL (Full Screen) ── -->
    <Teleport to="body">
      <div v-if="showProofModal"
        @click="closeProof"
        class="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">

        <!-- Close button -->
        <button @click="closeProof"
          class="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition z-10">
          <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <!-- Image -->
        <img :src="proofUrl"
          @click.stop
          alt="Proof"
          class="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
      </div>
    </Teleport>

    <!-- Delete confirmation -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Permanently Delete Khata Entry?"
      message="This action cannot be undone. The khata entry will be permanently removed from your account."
      info-message="WARNING: This will permanently delete this khata entry and remove it from all reports and statistics. This action is irreversible."
      confirm-text="Delete Permanently"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm" />

  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { Link, usePage, router, useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ProofUpload from '@/components/ProofUpload.vue'
import axios from 'axios'

const props = defineProps({
  customer: Object
})

const transactions = ref([])
const currentBalance = ref(0)
const dataLoading = ref(true)
const showModal = ref(false)
const showProofModal = ref(false)
const proofUrl = ref('')
const loading = ref(false)
const editTarget = ref(null)
const modalAction = ref('sent')
const banks = ref([])
const proofFile = ref(null)
const proofPreviewUrl = ref(null)
const expandedId = ref(null)
const showMoreDetails = ref(false)

// Delete state
const showDeleteConfirm = ref(false)
const deleteTarget = ref(null)

const form = ref({
  amount: '',
  source: 'cash',
  bank_id: '',
  target_date: new Date().toISOString().split('T')[0],
  notes: ''
})

// Computed stats
const totalSent = computed(() => {
  return transactions.value.reduce((sum, t) => sum + t.sent, 0)
})

const totalReceived = computed(() => {
  return transactions.value.reduce((sum, t) => sum + t.received, 0)
})

async function fetchData() {
  dataLoading.value = true
  try {
    const response = await axios.get(`/khata/${props.customer.id}/details/data`)
    transactions.value = response.data.transactions
    currentBalance.value = response.data.current_balance
  } catch (error) {
    console.error('Error fetching khata data:', error)
  } finally {
    dataLoading.value = false
  }
}

async function fetchBanks() {
  try {
    const response = await axios.get('/banks/data')
    banks.value = response.data.banks
  } catch (error) {
    console.error('Error fetching banks:', error)
  }
}

function openAdd(action) {
  editTarget.value = null
  modalAction.value = action
  form.value = {
    amount: '',
    source: 'cash',
    bank_id: '',
    target_date: new Date().toISOString().split('T')[0],
    notes: ''
  }
  proofFile.value = null
  proofPreviewUrl.value = null
  showMoreDetails.value = false
  showModal.value = true
}

function openEdit(transaction) {
  editTarget.value = transaction
  modalAction.value = transaction.sent > 0 ? 'sent' : 'received'
  form.value = {
    amount: transaction.original_amount,
    source: transaction.source || 'cash',
    bank_id: transaction.bank_id || '',
    target_date: transaction.target_date,
    notes: transaction.notes || ''
  }
  proofFile.value = null
  proofPreviewUrl.value = transaction.proof_url || null
  showMoreDetails.value = true // Show details when editing
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editTarget.value = null
  showMoreDetails.value = false
}

function submitForm() {
  loading.value = true

  const payload = {
    ...form.value,
    action: modalAction.value
  }

  if (proofFile.value) {
    payload.proof = proofFile.value
  }

  if (!editTarget.value) {
    payload.customer_id = props.customer.id
  }

  const req = useForm({ khata: payload })
  const opts = {
    onSuccess: () => {
      closeModal()
      fetchData()
    },
    onError: (errors) => {
      console.error('Error saving khata entry:', errors)
      alert('Error saving entry. Please try again.')
    },
    onFinish: () => {
      loading.value = false
    },
  }

  if (editTarget.value) {
    req.patch(`/khata/${editTarget.value.id}`, opts)
  } else {
    req.post('/khata', opts)
  }
}

function openProof(url) {
  proofUrl.value = url
  showProofModal.value = true
}

function closeProof() {
  showProofModal.value = false
  proofUrl.value = ''
}

function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Delete khata entry ─────────────────────────────
function openDeleteConfirm(transaction) {
  deleteTarget.value = transaction
  showDeleteConfirm.value = true
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  deleteTarget.value = null
}

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`/khata/${deleteTarget.value.id}`, {
      onSuccess: () => {
        closeDeleteConfirm()
        fetchData()
        resolve()
      },
      onError: () => {
        resolve()
      }
    })
  })
}

function formatNum(n) {
  return n.toLocaleString('en-IN')
}

function formatDate(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatDateShort(dateStr) {
  const date = new Date(dateStr)
  const day = date.getDate()
  const month = date.getMonth() + 1 // getMonth() returns 0-11
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

function getDaySuffix(day) {
  if (day >= 11 && day <= 13) return 'th'
  switch (day % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

function getCurrencySymbol(currency) {
  if (!currency) return 'PKR'

  // Handle both string and number values (enum can be number)
  const currencyMap = {
    0: 'PKR',      // pkr enum
    1: 'USD',      // usd enum
    2: 'EUR',      // euro enum
    'pkr': 'PKR',
    'usd': 'USD',
    'euro': 'EUR'
  }

  return currencyMap[currency] || String(currency).toUpperCase()
}

onMounted(() => {
  fetchData()
  fetchBanks()
})
</script>
